/**
 * Data-entry validation, shared by the server (authoritative) and the offline client (§5.2), so a value is
 * judged by exactly the same rules wherever it is typed, pasted, scanned or posted to the API (LIM-018).
 *
 * PROJECT_SPEC.md §6.1 "Data-entry validation: type, range, format masks, plausibility cross-checks,
 * forbidden-value lists, duplicate-entry detection"; LIM-006…LIM-018; DI-005 (the capture time is the
 * server's, never the client's, so this module never accepts one).
 */
import { D, type Dec, dec, toPlain } from "../calc/decimal.js";
import type { MbrContent } from "../mbr/content.js";
import { classify, type Verdict } from "../mbr/simulate.js";
import { resolveLocal } from "../time/zone.js";
import { type EntryContext, entryRuleRejection } from "./entry-rules.js";

type Step = MbrContent["procedure"]["steps"][number];
export type Parameter = Step["parameters"][number];

/** LIM-012: the store is numeric(38,9); anything beyond that is refused rather than wrapped or truncated. */
export const MAX_MAGNITUDE = new D(10).pow(29); // 38 digits total, 9 of them fractional

export type RejectionCode =
  | "REQUIRED"
  | "NOT_A_NUMBER"
  | "NEGATIVE_NOT_ALLOWED"
  | "OUT_OF_RANGE"
  | "TOO_PRECISE"
  | "TOO_LONG"
  | "FORBIDDEN_VALUE"
  | "NOT_A_DATE"
  | "FUTURE_NOT_ALLOWED"
  | "TIME_NEVER_HAPPENED"
  | "TIME_AMBIGUOUS"
  | "WRONG_TYPE"
  | "FORMAT_MISMATCH"
  | "IMPLAUSIBLE"
  | "CROSS_CHECK_FAILED"
  | "DUPLICATE_ENTRY"
  | "UNKNOWN_PARAMETER";

export interface Rejection {
  code: RejectionCode;
  /** A message an operator can act on, never a stack trace or a raw driver error (ERR-001). */
  message: string;
}

export interface AcceptedValue {
  ok: true;
  /** Canonical stored form: decimals as plain strings at the parameter's declared precision (CALC-005). */
  value: string;
  numeric?: Dec;
  verdict: Verdict;
  /** True when the value breaches the specification and the recipe says to stop rather than warn (LIM-006). */
  blocked: boolean;
  /** Set when the parameter rounds the entry; the raw entry is kept alongside the stored value (DI-009, LIM-011). */
  rawEntry?: string;
}
export type ValidationResult = AcceptedValue | { ok: false; rejection: Rejection };

const reject = (code: RejectionCode, message: string): ValidationResult => ({
  ok: false,
  rejection: { code, message },
});

/** Physical quantities cannot be negative; a difference or an offset can. Declared per parameter. */
const negativeAllowed = (p: Parameter): boolean => p.config["allowNegative"] === true;

/** ASCII digits only: a unicode digit is not a number an operator meant to type (LIM-008). */
const NUMERIC = /^-?\d+(\.\d+)?$/;

function validateDecimal(p: Parameter, raw: string, onOutOfSpec: string): ValidationResult {
  const trimmed = raw.trim();
  if (!NUMERIC.test(trimmed))
    return reject("NOT_A_NUMBER", `${p.label} takes a number, for example 12.500. Received “${raw}”.`);
  const value = dec(trimmed);
  if (!negativeAllowed(p) && value.isNegative())
    return reject("NEGATIVE_NOT_ALLOWED", `${p.label} cannot be negative.`);
  if (value.abs().gte(MAX_MAGNITUDE))
    return reject("OUT_OF_RANGE", `${p.label} is larger than this record can hold. Check the entry.`);
  const decimals = (trimmed.split(".")[1] ?? "").length;
  if (decimals > p.precision)
    // never silently truncate: the operator is told, and re-enters (LIM-011)
    return reject(
      "TOO_PRECISE",
      `${p.label} is recorded to ${p.precision} decimal place${p.precision === 1 ? "" : "s"}; “${trimmed}” has ${decimals}.`,
    );
  if (p.dataType === "INTEGER" && decimals > 0) return reject("TOO_PRECISE", `${p.label} is a whole number.`);
  const stored = toPlain(value, { places: p.precision, mode: p.rounding });
  const verdict = classify(value, p.limits);
  const blocked = onOutOfSpec === "BLOCK" && verdict === "OOS";
  const out: AcceptedValue = { ok: true, value: stored, numeric: value, verdict, blocked };
  if (stored !== trimmed) out.rawEntry = trimmed; // DI-009: the entry as made is kept beside the stored form
  return out;
}

function validateText(p: Parameter, raw: string): ValidationResult {
  const max = p.maxLength ?? 4000;
  // LIM-017: refuse, never truncate — a truncated batch record is a falsified one
  if (raw.length > max)
    return reject("TOO_LONG", `${p.label} allows at most ${max} characters; ${raw.length} were entered.`);
  return { ok: true, value: raw, verdict: "NO_LIMITS", blocked: false };
}

const HAS_OFFSET = /(Z|[+-]\d{2}:\d{2})$/;

/**
 * A wall-clock time typed without an offset is read in the SITE zone and resolved without guessing
 * (TIME-008/009; 21 CFR 11.10(e)): the hour a spring-forward skips never happened and is refused; the hour a
 * fall-back repeats is ambiguous and is refused until the person adds the offset. Never the process's own zone.
 */
function resolveWallTime(p: Parameter, iso: string, siteTz: string): ValidationResult | Date {
  const m = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})(?::(\d{2}))?(\.\d{1,6})?$/.exec(iso);
  if (!m) return reject("NOT_A_DATE", `${p.label}: “${iso}” is not a real date.`);
  const r = resolveLocal(`${m[1]}T${m[2]}:${m[3] ?? "00"}`, siteTz);
  if (r.kind === "SKIPPED") return reject("TIME_NEVER_HAPPENED", `${p.label}: ${r.message}`);
  if (r.kind === "AMBIGUOUS")
    return reject(
      "TIME_AMBIGUOUS",
      `${p.label}: ${iso} happened twice in ${siteTz} (${r.earlier} and ${r.later}). Enter it with its UTC offset or as UTC.`,
    );
  const ms = m[4] ? Math.round(Number(`0${m[4]}`) * 1000) : 0;
  return new Date(new Date(r.utc).getTime() + ms);
}

/** LIM-016: a date field refuses an impossible date, and a future one where the recipe forbids it. */
function validateDateTime(p: Parameter, raw: string, now: Date, siteTz: string): ValidationResult {
  const iso = raw.trim();
  if (!/^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2}(:\d{2}(\.\d{1,6})?)?(Z|[+-]\d{2}:\d{2})?)?$/.test(iso))
    return reject("NOT_A_DATE", `${p.label} takes a date such as 2026-09-07 or 2026-09-07T14:32:00Z.`);
  const timed = iso.includes("T") || iso.includes(" ");
  let parsed: Date;
  if (timed && !HAS_OFFSET.test(iso)) {
    const wall = resolveWallTime(p, iso, siteTz);
    if (!(wall instanceof Date)) return wall;
    parsed = wall;
  } else parsed = new Date(timed ? iso : `${iso}T00:00:00Z`);
  // Date accepts 2026-02-31 by rolling into March: compare the calendar parts back to catch it
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number) as [number, number, number];
  const cal = new Date(Date.UTC(y, m - 1, d));
  if (cal.getUTCFullYear() !== y || cal.getUTCMonth() + 1 !== m || cal.getUTCDate() !== d)
    return reject("NOT_A_DATE", `${p.label}: “${iso.slice(0, 10)}” is not a real date.`);
  if (Number.isNaN(parsed.getTime())) return reject("NOT_A_DATE", `${p.label}: “${raw}” is not a real date.`);
  if (p.config["futureAllowed"] === false && parsed.getTime() > now.getTime())
    return reject("FUTURE_NOT_ALLOWED", `${p.label} cannot be in the future.`);
  return { ok: true, value: parsed.toISOString(), verdict: "NO_LIMITS", blocked: false };
}

function validateBoolean(p: Parameter, raw: string): ValidationResult {
  const v = raw.trim().toLowerCase();
  if (["true", "yes", "1", "pass"].includes(v))
    return { ok: true, value: "true", verdict: "NO_LIMITS", blocked: false };
  if (["false", "no", "0", "fail"].includes(v))
    return { ok: true, value: "false", verdict: "NO_LIMITS", blocked: false };
  return reject("WRONG_TYPE", `${p.label} takes yes or no.`);
}

/**
 * Validates one entry against its parameter as declared in the batch's frozen snapshot.
 * `now` is passed in rather than read, so the same call is reproducible and the client cannot influence
 * the recorded time (DI-005 — the capture timestamp is written by the database, not by this function).
 */
export function validateEntry(
  p: Parameter,
  raw: string,
  opts: { onOutOfSpec?: string; now?: Date; siteTz?: string; context?: EntryContext } = {},
): ValidationResult {
  const value = raw ?? "";
  if (value.trim() === "")
    return p.mandatory
      ? reject("REQUIRED", `${p.label} is required.`)
      : { ok: true, value: "", verdict: "NO_LIMITS", blocked: false };
  const forbidden = (p.config["forbiddenValues"] as string[] | undefined) ?? [];
  if (forbidden.some((f) => f.toLowerCase() === value.trim().toLowerCase()))
    return reject("FORBIDDEN_VALUE", `“${value.trim()}” is not an acceptable entry for ${p.label}.`);
  const rule = entryRuleRejection(p.label, p.dataType, p.config, value, opts.context);
  if (rule) return reject(rule.code, rule.message);
  const onOutOfSpec = opts.onOutOfSpec ?? "RAISE_DEVIATION";
  switch (p.dataType) {
    case "DECIMAL":
    case "INTEGER":
      return validateDecimal(p, value, onOutOfSpec);
    case "TEXT":
      return validateText(p, value);
    case "DATETIME":
      return validateDateTime(p, value, opts.now ?? new Date(0), opts.siteTz ?? "UTC");
    case "BOOLEAN":
      return validateBoolean(p, value);
    default:
      return reject("WRONG_TYPE", `${p.label} has an unsupported type.`);
  }
}

/** Finds a parameter in a frozen snapshot; the recipe, not the request, decides what a step may record. */
export function findParameter(content: MbrContent, stepId: string, code: string): Parameter | undefined {
  return content.procedure.steps.find((s) => s.id === stepId)?.parameters.find((p) => p.code === code);
}

/** The out-of-specification policy declared on the step (MANUAL_ENTRY config), defaulting to raising a deviation. */
export function outOfSpecPolicy(content: MbrContent, stepId: string): string {
  const step = content.procedure.steps.find((s) => s.id === stepId);
  return (step?.config?.["onOutOfSpec"] as string | undefined) ?? "RAISE_DEVIATION";
}
